import * as maplibregl from 'maplibre-gl';
import mapLibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import type {
  ErrorEvent as MapLibreErrorEvent,
  GeolocateErrorEvent,
  GeolocatePositionEvent,
  LayerSpecification,
  Map as MapLibreMap,
  MapEventType,
  MapMouseEvent,
  StyleSpecification,
} from 'maplibre-gl';

import { maximumPitch } from '../../domain/camera.ts';
import { createCameraState, createLngLat } from '../../domain/coordinates.ts';
import {
  classifyProviderFailure,
  createApplicationError,
  type ApplicationError,
  type ProviderId,
} from '../../domain/errors.ts';
import {
  createLayerVisibilityState,
  defaultBasemap,
  type BasemapId,
  type LayerGroupDefinition,
  type MapContentSourceId,
  type SemanticLayerPart,
} from '../../domain/layers.ts';
import { trailAnchor, trailBounds, trailCoordinates } from '../../domain/trail-selection.ts';
import { toCanonicalTrailCollection } from '../../domain/trails.ts';
import { createTrailId, type Trail, type TrailDifficulty } from '../../domain/trails.ts';
import {
  createLocalization,
  type MessageId,
  type SupportedLocale,
} from '../../localization/locale.ts';
import type {
  GeoJsonFeatureCollection,
  MapCommand,
  MapCommandResult,
  MapEngineAdapter,
  MapEvent,
  MapMountOptions,
  MapSession,
  MapState,
} from '../../map/contracts.ts';

maplibregl.setWorkerUrl(mapLibreWorkerUrl);

export interface MapLibreProviderConfig {
  readonly glyphs: string;
  readonly satelliteTiles: string;
  readonly streetsStyle: string;
  readonly swisstopoForestTiles: string;
  readonly swisstopoGrayTiles: string;
  readonly terrainTiles: string;
  readonly zugerwaldTiles: string;
}

export const defaultMapLibreProviders = Object.freeze({
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  satelliteTiles:
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  streetsStyle: 'https://tiles.openfreemap.org/styles/liberty',
  swisstopoForestTiles:
    'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swisstlm3d-wald/default/current/3857/{z}/{x}/{y}.png',
  swisstopoGrayTiles:
    'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-grau/default/current/3857/{z}/{x}/{y}.jpeg',
  terrainTiles: 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
  zugerwaldTiles:
    'https://geo.zugerwald.ch/geoserver/gwc/service/tms/1.0.0/DBI%3Abike@WebMercatorQuad@pbf/{z}/{x}/{y}.pbf',
} satisfies MapLibreProviderConfig);

const capabilities = Object.freeze({
  flat: true,
  terrain: true,
  globe: false,
  objects3d: false,
  geolocation: true,
  pointPicking: true,
});

export const mapLibreTerrainExaggeration = 1;
export const mapLibreGeolocationOptions = Object.freeze({
  positionOptions: Object.freeze({ enableHighAccuracy: true, timeout: 10_000 }),
  trackUserLocation: true,
  showAccuracyCircle: true,
  showUserLocation: true,
  fitBoundsOptions: Object.freeze({ maxZoom: 16 }),
});

export function geolocationAvailabilityFailure(
  secureContext: boolean,
  supported: boolean,
): 'insecure' | 'unsupported' | undefined {
  if (!secureContext) return 'insecure';
  return supported ? undefined : 'unsupported';
}

export function geolocationFailureReason(code: number): 'denied' | 'timeout' | 'unavailable' {
  return code === 1 ? 'denied' : code === 3 ? 'timeout' : 'unavailable';
}

interface FixedFillSnapshot {
  readonly fillColor: unknown;
  readonly fillOpacity: unknown;
  readonly filter: unknown;
}

export interface MapLibreRecoverySnapshot {
  readonly basemap: BasemapId;
  readonly boundsMask: Readonly<FixedFillSnapshot> | null;
  readonly cantonOverlay: Readonly<FixedFillSnapshot> | null;
  readonly handlerCount: number;
  readonly layerIds: readonly string[];
  readonly legacyLabelLayout: Readonly<{
    readonly bikeRegionAnchor: unknown;
    readonly zugerbergbahnLetterSpacing: unknown;
    readonly zugerbergbahnPadding: unknown;
  }>;
  readonly locale: MapState['locale'];
  readonly pitch: number;
  readonly presentation: MapState['presentation'];
  readonly selectedTrailId: MapState['selectedTrailId'];
  readonly sourceIds: readonly string[];
  readonly styleLoadCount: number;
  readonly terrain: Readonly<{ source: string; exaggeration: number }> | null;
  readonly visibleLayerIds: readonly string[];
}

export interface MapLibreTestHooks {
  readonly styleRecovered?: (snapshot: MapLibreRecoverySnapshot) => void;
}

const swisstopoAttribution =
  '&copy; <a href="https://www.swisstopo.admin.ch/" target="_blank" rel="noopener">swisstopo</a>';
const esriAttribution =
  '&copy; <a href="https://www.esri.com/" target="_blank" rel="noopener">Esri</a>';
const zugerwaldAttribution =
  '<a href="https://www.zugerwald.ch/" target="_blank" rel="noopener">Zugerwald</a>';
const openStreetMapAttribution =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a>';
const terrainAttribution =
  '<a href="https://github.com/tilezen/joerd/blob/master/docs/attribution.md" target="_blank" rel="noopener">Mapzen terrain data contributors</a>';
const fixedContextSourceIds = ['cantons', 'maximum-bounds-mask'] as const;
const fixedContextLayerIds = ['cantons-fill', 'maximum-bounds-mask-fill'] as const;

const mapLibreLocales = Object.freeze({
  de: {
    'FullscreenControl.Enter': 'Vollbild öffnen',
    'FullscreenControl.Exit': 'Vollbild schließen',
    'GeolocateControl.FindMyLocation': 'Eigenen Standort anzeigen',
    'Map.Title': 'Karte',
    'NavigationControl.ResetBearing':
      'Ziehen, um die Karte zu drehen; klicken, um Norden auszurichten',
    'NavigationControl.ZoomIn': 'Vergrößern',
    'NavigationControl.ZoomOut': 'Verkleinern',
  },
  en: {
    'FullscreenControl.Enter': 'Enter fullscreen',
    'FullscreenControl.Exit': 'Exit fullscreen',
    'GeolocateControl.FindMyLocation': 'Show my location',
    'Map.Title': 'Map',
    'NavigationControl.ResetBearing': 'Drag to rotate map, click to reset north',
    'NavigationControl.ZoomIn': 'Zoom in',
    'NavigationControl.ZoomOut': 'Zoom out',
  },
  fr: {
    'FullscreenControl.Enter': 'Passer en plein écran',
    'FullscreenControl.Exit': 'Quitter le plein écran',
    'GeolocateControl.FindMyLocation': 'Afficher ma position',
    'Map.Title': 'Carte',
    'NavigationControl.ResetBearing':
      'Faire glisser pour tourner la carte, cliquer pour orienter au nord',
    'NavigationControl.ZoomIn': 'Zoom avant',
    'NavigationControl.ZoomOut': 'Zoom arrière',
  },
  it: {
    'FullscreenControl.Enter': 'Attiva schermo intero',
    'FullscreenControl.Exit': 'Esci da schermo intero',
    'GeolocateControl.FindMyLocation': 'Mostra la mia posizione',
    'Map.Title': 'Mappa',
    'NavigationControl.ResetBearing':
      'Trascina per ruotare la mappa, fai clic per orientarla a nord',
    'NavigationControl.ZoomIn': 'Ingrandisci',
    'NavigationControl.ZoomOut': 'Riduci',
  },
} satisfies Readonly<Record<SupportedLocale, Readonly<Record<string, string>>>>);

type MapLibreUiStrings = (typeof mapLibreLocales)[SupportedLocale];

const popupDifficultyMessageIds = {
  '3': 'difficulty.easy',
  '4': 'difficulty.medium',
  '9': 'difficulty.difficult',
  '10': 'difficulty.veryDifficult',
} as const satisfies Readonly<Record<TrailDifficulty, MessageId>>;

export function createTrailPopupContent(
  trail: Trail,
  locale: SupportedLocale,
  ownerDocument: Document = document,
): HTMLElement {
  const localization = createLocalization(locale);
  const content = ownerDocument.createElement('section');
  content.className = 'trail-popup';
  content.setAttribute('aria-label', localization.message('popup.title'));
  const title = ownerDocument.createElement('h2');
  title.className = 'popup-title';
  title.textContent = trail.title;
  content.append(title);
  const difficulty = ownerDocument.createElement('p');
  difficulty.className = 'popup-difficulty';
  const difficultyMarker = ownerDocument.createElement('span');
  difficultyMarker.className = 'popup-difficulty-marker';
  difficultyMarker.style.backgroundColor = mapLibreTrailDifficultyColors[trail.difficulty];
  difficultyMarker.setAttribute('aria-hidden', 'true');
  difficulty.append(
    difficultyMarker,
    localization.message(popupDifficultyMessageIds[trail.difficulty]),
  );
  content.append(difficulty);
  if (trail.externalUrl !== undefined) {
    const link = ownerDocument.createElement('a');
    link.className = 'popup-link';
    link.href = trail.externalUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = localization.message('popup.openTrailforks');
    const icon = ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '2');
    icon.setAttribute('stroke-linecap', 'round');
    icon.setAttribute('aria-hidden', 'true');
    const arrow = ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'path');
    arrow.setAttribute('d', 'M7 17L17 7M7 7h10v10');
    icon.append(arrow);
    link.append(icon);
    content.append(link);
  }
  return content;
}

function mapLibreUiLocale(locale: SupportedLocale): MapLibreUiStrings {
  return mapLibreLocales[locale];
}

interface LocalizedMapControls {
  readonly destroy: () => void;
  readonly fullscreen: maplibregl.FullscreenControl;
  readonly geolocation: maplibregl.GeolocateControl;
  readonly navigation: maplibregl.NavigationControl;
  readonly setLocale: (locale: SupportedLocale) => void;
}

function setControlLabel(host: HTMLElement, selector: string, label: string): void {
  const button = host.querySelector<HTMLButtonElement>(selector);
  if (button === null) return;
  button.title = label;
  button.setAttribute('aria-label', label);
}

function createLocalizedMapControls(
  host: HTMLElement,
  locale: SupportedLocale,
): LocalizedMapControls {
  const navigation = new maplibregl.NavigationControl();
  const fullscreen = new maplibregl.FullscreenControl();
  const geolocation = new maplibregl.GeolocateControl(mapLibreGeolocationOptions);
  let activeLocale = locale;
  const updateLabels = (): void => {
    const strings = mapLibreUiLocale(activeLocale);
    setControlLabel(host, '.maplibregl-ctrl-zoom-in', strings['NavigationControl.ZoomIn']);
    setControlLabel(host, '.maplibregl-ctrl-zoom-out', strings['NavigationControl.ZoomOut']);
    setControlLabel(host, '.maplibregl-ctrl-compass', strings['NavigationControl.ResetBearing']);
    setControlLabel(host, '.maplibregl-ctrl-geolocate', strings['GeolocateControl.FindMyLocation']);
    const fullscreenButton = host.querySelector<HTMLButtonElement>(
      '.maplibregl-ctrl-fullscreen, .maplibregl-ctrl-shrink',
    );
    if (fullscreenButton !== null) {
      const label = fullscreenButton.classList.contains('maplibregl-ctrl-shrink')
        ? strings['FullscreenControl.Exit']
        : strings['FullscreenControl.Enter'];
      fullscreenButton.title = label;
      fullscreenButton.setAttribute('aria-label', label);
    }
  };
  fullscreen.on('fullscreenstart', updateLabels);
  fullscreen.on('fullscreenend', updateLabels);
  return Object.freeze({
    navigation,
    fullscreen,
    geolocation,
    destroy(): void {
      fullscreen.off('fullscreenstart', updateLabels);
      fullscreen.off('fullscreenend', updateLabels);
    },
    setLocale(nextLocale: SupportedLocale): void {
      activeLocale = nextLocale;
      updateLabels();
    },
  });
}

export const mapLibreTrailDifficultyColors = Object.freeze({
  '3': '#46b414',
  '4': '#148cf0',
  '9': '#dc1313',
  '10': '#000000',
} as const);

function coordinateTuple(coordinate: readonly [number, number]): [number, number] {
  return [coordinate[0], coordinate[1]];
}

function rasterStyle(
  id: 'satellite' | 'swisstopo',
  tileUrl: string,
  glyphs: string,
): StyleSpecification {
  return {
    version: 8,
    glyphs,
    sources: {
      [id]: {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 256,
        maxzoom: 19,
        attribution: id === 'swisstopo' ? swisstopoAttribution : esriAttribution,
      },
    },
    layers: [
      {
        id: `${id}-background`,
        type: 'raster',
        source: id,
        ...(id === 'swisstopo'
          ? { paint: { 'raster-brightness-min': 0.4, 'raster-contrast': -0.2 } }
          : {}),
      },
    ],
  };
}

function styleForBasemap(
  basemap: BasemapId,
  providers: MapLibreProviderConfig,
): StyleSpecification | string {
  if (basemap === 'streets') return providers.streetsStyle;
  return rasterStyle(
    basemap,
    basemap === 'swisstopo' ? providers.swisstopoGrayTiles : providers.satelliteTiles,
    providers.glyphs,
  );
}

function providerFromError(event: MapLibreErrorEvent): ProviderId | undefined {
  const sourceId = (event as unknown as { readonly sourceId?: unknown }).sourceId;
  const text = `${typeof sourceId === 'string' ? sourceId : ''} ${event.error.message}`;
  if (/zugerwald/u.test(text)) return 'zugerwald';
  if (/terrain|hillshade/u.test(text)) return 'terrain';
  if (/openfreemap|streets/u.test(text)) return 'openfreemap';
  if (/swisstopo/u.test(text)) return 'swisstopo';
  if (/satellite|arcgis|esri/u.test(text)) return 'esri';
  return undefined;
}

export function classifyMapLibreProviderError(
  sourceId: string | undefined,
  message: string,
): ApplicationError {
  const event = { error: new Error(message), sourceId } as unknown as MapLibreErrorEvent;
  const provider = providerFromError(event);
  return provider === undefined
    ? createApplicationError({ code: 'unexpected', diagnostics: { operation: 'render' } })
    : classifyProviderFailure(event.error, { provider, operation: 'render' });
}

function sourceData(
  sourceId: MapContentSourceId,
  options: MapMountOptions,
): GeoJsonFeatureCollection {
  if (sourceId === 'canonical-trails') {
    return toCanonicalTrailCollection(options.content.selectableTrails);
  }
  const data = options.content.sourceData?.[sourceId];
  if (data === undefined) throw new TypeError(`canonical content source is missing: ${sourceId}`);
  return data;
}

function semanticSourceIds(options: MapMountOptions): Set<MapContentSourceId> {
  return new Set(options.content.layers.flatMap((group) => group.parts.map((part) => part.source)));
}

function requiredSourceIds(options: MapMountOptions): Set<string> {
  return new Set([
    'terrain-dem',
    ...fixedContextSourceIds,
    ...[...semanticSourceIds(options)].filter((sourceId) => sourceId !== 'zugerwald-vector'),
  ]);
}

function expectedOwnedSourceIds(options: MapMountOptions, basemap: BasemapId): Set<string> {
  return new Set([
    ...requiredSourceIds(options),
    ...(basemap === 'swisstopo' ? ['swisstopo-forest'] : []),
    ...semanticSourceIds(options),
  ]);
}

function expectedOwnedLayerIds(options: MapMountOptions, basemap: BasemapId): Set<string> {
  return new Set([
    ...(basemap === 'swisstopo' ? ['swisstopo-forest'] : []),
    ...(basemap === 'satellite' ? [] : ['hillshade']),
    ...fixedContextLayerIds,
    ...options.content.layers.flatMap((group) => group.parts.map((part) => part.id)),
  ]);
}

function validateContentSources(options: MapMountOptions): void {
  for (const sourceId of semanticSourceIds(options)) {
    if (sourceId !== 'zugerwald-vector') sourceData(sourceId, options);
  }
  sourceData('cantons', options);
}

function addApplicationSources(
  map: MapLibreMap,
  options: MapMountOptions,
  providers: MapLibreProviderConfig,
): void {
  for (const id of semanticSourceIds(options)) {
    if (map.getSource(id) !== undefined) continue;
    if (id === 'zugerwald-vector') {
      map.addSource(id, {
        type: 'vector',
        tiles: [providers.zugerwaldTiles],
        scheme: 'tms',
        maxzoom: 18,
        attribution: zugerwaldAttribution,
      });
      continue;
    }
    map.addSource(id, {
      type: 'geojson',
      data: sourceData(id, options) as never,
      ...(id === 'canonical-trails' ? { promoteId: 'trailId' } : {}),
      ...(id === 'zugerbergbahn' ? { attribution: openStreetMapAttribution } : {}),
    });
  }
}

function baseLayout(visible: boolean): Record<string, unknown> {
  return { visibility: visible ? 'visible' : 'none' };
}

function lineLayer(
  part: SemanticLayerPart,
  visible: boolean,
  paint: Record<string, unknown>,
  extra?: Partial<LayerSpecification>,
): LayerSpecification {
  return {
    id: part.id,
    type: 'line',
    source: part.source,
    layout: { ...baseLayout(visible), 'line-cap': 'round', 'line-join': 'round' },
    paint,
    ...extra,
  } as LayerSpecification;
}

function layerForPart(part: SemanticLayerPart, visible: boolean): LayerSpecification {
  const lineStyles: Readonly<Record<string, Record<string, unknown>>> = {
    'daily-network': { 'line-color': '#888888', 'line-width': 3, 'line-opacity': 0.8 },
    'cycling-highway': { 'line-color': '#888888', 'line-width': 5, 'line-opacity': 0.8 },
    'recreation-bike': { 'line-color': '#a87000', 'line-width': 6, 'line-opacity': 0.8 },
    'recreation-route': { 'line-color': '#888888', 'line-width': 3, 'line-opacity': 0.8 },
    'planned-cycling-casing': { 'line-color': '#fff', 'line-width': 8, 'line-opacity': 0.92 },
    'planned-cycling-line': {
      'line-color': '#2563eb',
      'line-width': 6,
      'line-opacity': 1,
      'line-dasharray': [3, 2],
    },
    'planned-bike-casing': { 'line-color': '#fff', 'line-width': 11, 'line-opacity': 0.94 },
    'planned-bike-line': {
      'line-color': '#e85d04',
      'line-width': 6,
      'line-opacity': 1,
      'line-dasharray': [2, 1.25],
    },
    'zugerwald-forest-road': { 'line-color': '#8b4513', 'line-width': 3, 'line-opacity': 0.8 },
    'zugerwald-bike-route': {
      'line-color': '#8b4513',
      'line-width': 3,
      'line-opacity': 0.8,
      'line-dasharray': [2, 2],
    },
    'zugerbergbahn-casing': { 'line-color': '#fff', 'line-width': 7, 'line-opacity': 0.9 },
    'zugerbergbahn-route': { 'line-color': '#64748b', 'line-width': 4, 'line-opacity': 0.85 },
  };
  if (part.id === 'trail-hit-target') {
    return lineLayer(part, visible, { 'line-color': 'transparent', 'line-width': 20 });
  }
  if (part.id === 'trail-visible') {
    const colors = mapLibreTrailDifficultyColors;
    return lineLayer(part, visible, {
      'line-color': [
        'match',
        ['get', 'difficulty'],
        '3',
        colors['3'],
        '4',
        colors['4'],
        '9',
        colors['9'],
        '10',
        colors['10'],
        'transparent',
      ],
      'line-width': 3,
      'line-opacity': 1,
    });
  }
  if (part.id === 'trail-selection') {
    return lineLayer(part, visible, {
      'line-color': '#facc15',
      'line-width': 7,
      'line-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0],
    });
  }
  if (part.id === 'bike-region-fill') {
    return {
      id: part.id,
      type: 'fill',
      source: part.source,
      layout: baseLayout(visible),
      paint: { 'fill-color': '#008000', 'fill-opacity': 0.08 },
    };
  }
  if (part.id === 'bike-region-outline') {
    return lineLayer(part, visible, {
      'line-color': '#008000',
      'line-width': 5,
      'line-opacity': 0.8,
    });
  }
  if (part.id === 'bike-region-label') {
    return symbolLayer(part, visible, ['get', 'title'], [0, 0], '#008000');
  }
  if (part.id === 'zugerbergbahn-route-label') {
    return symbolLayer(part, visible, ['get', 'name'], [0, 0], '#475569', 'line', undefined, {
      'text-letter-spacing': 0.04,
      'text-max-angle': 45,
      'text-padding': 4,
    });
  }
  if (part.id === 'zugerbergbahn-station-labels') {
    return symbolLayer(part, visible, ['get', 'name'], [0, 1.1], '#475569', undefined, 'Point', {
      'text-anchor': 'top',
    });
  }
  if (part.id === 'zugerbergbahn-stations') {
    return {
      id: part.id,
      type: 'circle',
      source: part.source,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: baseLayout(visible),
      paint: {
        'circle-radius': 5,
        'circle-color': '#64748b',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    };
  }
  const sourceLayer =
    part.id === 'zugerwald-forest-road'
      ? 'weg_waldstrasse_20251125'
      : part.id === 'zugerwald-bike-route'
        ? 'weg_bikeroute_20251125'
        : undefined;
  return lineLayer(part, visible, lineStyles[part.id] ?? {}, {
    ...(sourceLayer === undefined ? {} : { 'source-layer': sourceLayer }),
    ...(part.id.startsWith('zugerbergbahn-')
      ? { filter: ['==', ['geometry-type'], 'LineString'] as never }
      : {}),
  });
}

function symbolLayer(
  part: SemanticLayerPart,
  visible: boolean,
  textField: unknown,
  textOffset: readonly [number, number],
  color: string,
  placement?: 'line',
  geometryType?: 'Point',
  layout?: Readonly<Record<string, unknown>>,
): LayerSpecification {
  return {
    id: part.id,
    type: 'symbol',
    source: part.source,
    ...(geometryType === undefined
      ? {}
      : { filter: ['==', ['geometry-type'], geometryType] as never }),
    layout: {
      ...baseLayout(visible),
      ...(placement === undefined ? {} : { 'symbol-placement': placement }),
      'text-field': textField as never,
      'text-font': ['Noto Sans Bold'],
      'text-size': part.id === 'bike-region-label' ? 14 : 11,
      'text-offset': [...textOffset],
      ...layout,
    },
    paint: { 'text-color': color, 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
  };
}

function installBasemapTreatments(
  map: MapLibreMap,
  basemap: BasemapId,
  providers: MapLibreProviderConfig,
): void {
  const firstSymbolId = map.getStyle().layers.find(({ type }) => type === 'symbol')?.id;
  if (map.getSource('terrain-dem') === undefined) {
    map.addSource('terrain-dem', {
      type: 'raster-dem',
      tiles: [providers.terrainTiles],
      encoding: 'terrarium',
      tileSize: 256,
      maxzoom: 15,
      attribution: terrainAttribution,
    });
  }
  map.setTerrain({ source: 'terrain-dem', exaggeration: mapLibreTerrainExaggeration });
  if (basemap === 'swisstopo' && map.getSource('swisstopo-forest') === undefined) {
    map.addSource('swisstopo-forest', {
      type: 'raster',
      tiles: [providers.swisstopoForestTiles],
      tileSize: 256,
      maxzoom: 17,
      attribution: swisstopoAttribution,
    });
  }
  if (basemap === 'swisstopo' && map.getLayer('swisstopo-forest') === undefined) {
    map.addLayer(
      {
        id: 'swisstopo-forest',
        type: 'raster',
        source: 'swisstopo-forest',
        paint: { 'raster-opacity': 0.4, 'raster-saturation': -0.3 },
      },
      firstSymbolId,
    );
  }
  if (basemap !== 'satellite' && map.getLayer('hillshade') === undefined) {
    map.addLayer(
      {
        id: 'hillshade',
        type: 'hillshade',
        source: 'terrain-dem',
        paint: {
          'hillshade-illumination-direction': 315,
          'hillshade-exaggeration': 0.4,
          'hillshade-shadow-color': '#000',
          'hillshade-highlight-color': '#fff',
          'hillshade-accent-color': '#000',
        },
      },
      firstSymbolId,
    );
  }
}

function rectangularRing(
  west: number,
  south: number,
  east: number,
  north: number,
): [number, number][] {
  return [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
    [west, south],
  ];
}

function installFixedContext(map: MapLibreMap, options: MapMountOptions): void {
  if (map.getSource('cantons') === undefined) {
    map.addSource('cantons', {
      type: 'geojson',
      data: sourceData('cantons', options) as never,
    });
  }
  if (map.getSource('maximum-bounds-mask') === undefined) {
    const { southWest, northEast } = options.maximumBounds;
    map.addSource('maximum-bounds-mask', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [
            rectangularRing(-180, -85, 180, 85),
            rectangularRing(southWest[0], southWest[1], northEast[0], northEast[1]).reverse(),
          ],
        },
      },
    });
  }
  const firstSymbolId = map.getStyle().layers.find(({ type }) => type === 'symbol')?.id;
  if (map.getLayer('cantons-fill') === undefined) {
    map.addLayer(
      {
        id: 'cantons-fill',
        type: 'fill',
        source: 'cantons',
        filter: ['!=', ['get', 'KTKZ'], 'ZG'],
        paint: {
          'fill-color': '#808080',
          'fill-opacity': 0.8,
          'fill-outline-color': 'rgba(0,0,0,0)',
        },
      },
      firstSymbolId,
    );
  }
  if (map.getLayer('maximum-bounds-mask-fill') === undefined) {
    map.addLayer(
      {
        id: 'maximum-bounds-mask-fill',
        type: 'fill',
        source: 'maximum-bounds-mask',
        paint: { 'fill-color': '#fff', 'fill-opacity': 1 },
      },
      firstSymbolId,
    );
  }
}

function installApplicationLayers(
  map: MapLibreMap,
  options: MapMountOptions,
  state: MapState,
  providers: MapLibreProviderConfig,
): void {
  installBasemapTreatments(map, state.basemap, providers);
  installFixedContext(map, options);
  addApplicationSources(map, options, providers);
  const parts = options.content.layers
    .flatMap((group) => group.parts.map((part) => ({ group, part })))
    .sort((left, right) => left.part.renderOrder - right.part.renderOrder);
  for (const { group, part } of parts) {
    if (map.getLayer(part.id) === undefined) {
      map.addLayer(layerForPart(part, state.layerVisibility[group.id]));
    }
  }
  if (state.selectedTrailId !== null) {
    map.setFeatureState(
      { source: 'canonical-trails', id: state.selectedTrailId },
      { selected: true },
    );
  }
}

function initialState(options: MapMountOptions): MapState {
  const center =
    options.initialView.type === 'camera'
      ? options.initialView.camera.center
      : createLngLat(
          (options.initialView.bounds.southWest[0] + options.initialView.bounds.northEast[0]) / 2,
          (options.initialView.bounds.southWest[1] + options.initialView.bounds.northEast[1]) / 2,
        );
  return Object.freeze({
    camera:
      options.initialView.type === 'camera'
        ? options.initialView.camera
        : createCameraState({
            center,
            zoom: options.initialView.maximumZoom ?? 12,
            bearing: 0,
            pitch: 0,
          }),
    basemap: defaultBasemap().id,
    layerVisibility: createLayerVisibilityState(
      Object.fromEntries(options.content.layers.map((layer) => [layer.id, layer.defaultVisible])),
    ),
    presentation:
      options.initialView.type === 'camera' && options.initialView.camera.pitch > 0
        ? 'terrain'
        : 'flat',
    locale: options.locale,
    interactionMode: 'browse',
    selectedTrailId: null,
  });
}

function createSession(
  map: MapLibreMap,
  options: MapMountOptions,
  providers: MapLibreProviderConfig,
  controls: LocalizedMapControls,
  testHooks: MapLibreTestHooks,
): MapSession {
  const listeners = new Set<(event: MapEvent) => void>();
  const queued: MapCommand[] = [];
  const cleanup: Array<() => void> = [];
  cleanup.push(controls.destroy);
  let state = initialState(options);
  let ready = false;
  let destroyed = false;
  let activeMovement = false;
  let cameraEventsEnabled = false;
  let cameraEventsSuppressed = false;
  let handlerCount = 0;
  let styleLoadCount = 0;
  let selectedTrailPopup: maplibregl.Popup | undefined;
  let selectedTrailCoordinate: MapState['camera']['center'] | undefined;
  let suppressPopupClose = false;

  const emit = (event: MapEvent): void => {
    if (!destroyed) for (const listener of listeners) listener(event);
  };
  const camera = (): MapState['camera'] =>
    createCameraState({
      center: createLngLat(map.getCenter().lng, map.getCenter().lat),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    });
  const on = <T extends keyof MapEventType>(
    type: T,
    listener: (event: MapEventType[T]) => void,
  ): void => {
    map.on(type, listener);
    handlerCount += 1;
    cleanup.push(() => {
      map.off(type, listener);
    });
  };
  const becomeReady = (): void => {
    if (ready) return;
    ready = true;
    state = Object.freeze({ ...state, camera: camera() });
    emit({ type: 'ready', state, capabilities });
    for (const command of queued.splice(0)) void apply(command);
  };
  const requiredSources = requiredSourceIds(options);
  const becomeReadyWhenApplicationSourcesLoad = (): void => {
    if (ready) return;
    if (
      [...requiredSources].every(
        (sourceId) => map.getSource(sourceId) !== undefined && map.isSourceLoaded(sourceId),
      )
    ) {
      becomeReady();
    }
  };
  const enableCameraEvents = (): void => {
    cameraEventsEnabled = true;
  };

  const localizePopupClose = (popup: maplibregl.Popup): void => {
    const close = popup
      .getElement()
      .querySelector<HTMLButtonElement>('.maplibregl-popup-close-button');
    if (close === null) return;
    const label = createLocalization(state.locale).message('popup.close');
    close.title = label;
    close.setAttribute('aria-label', label);
  };

  const removePopupSilently = (): void => {
    if (selectedTrailPopup === undefined) return;
    const popup = selectedTrailPopup;
    selectedTrailPopup = undefined;
    suppressPopupClose = true;
    popup.remove();
    suppressPopupClose = false;
  };

  const clearTrailSelection = (emitEvent = true): void => {
    removePopupSilently();
    if (state.selectedTrailId !== null && map.getSource('canonical-trails') !== undefined) {
      map.removeFeatureState({ source: 'canonical-trails', id: state.selectedTrailId }, 'selected');
    }
    state = Object.freeze({ ...state, selectedTrailId: null });
    selectedTrailCoordinate = undefined;
    if (emitEvent) emit({ type: 'trail-selection-cleared' });
  };

  const openTrailPopup = (trail: Trail, coordinate: MapState['camera']['center']): void => {
    removePopupSilently();
    const popup = new maplibregl.Popup({ focusAfterOpen: false, offset: 12, maxWidth: '240px' })
      .setLngLat(coordinateTuple(coordinate))
      .setDOMContent(createTrailPopupContent(trail, state.locale))
      .addTo(map);
    selectedTrailPopup = popup;
    selectedTrailCoordinate = coordinate;
    localizePopupClose(popup);
    popup.on('close', () => {
      if (suppressPopupClose || selectedTrailPopup !== popup) return;
      selectedTrailPopup = undefined;
      clearTrailSelection();
    });
  };

  const trailFitPadding = (): Readonly<{
    top: number;
    bottom: number;
    left: number;
    right: number;
  }> =>
    map.getContainer().clientWidth <= 640
      ? { top: 100, bottom: 220, left: 30, right: 30 }
      : { top: 100, bottom: 100, left: 80, right: 80 };

  const trailFitsViewport = (
    trail: Trail,
    padding: ReturnType<typeof trailFitPadding>,
  ): boolean => {
    const canvas = map.getCanvas();
    return trailCoordinates(trail).every((coordinate) => {
      const point = map.project(coordinateTuple(coordinate));
      return (
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        point.x >= padding.left &&
        point.x <= canvas.clientWidth - padding.right &&
        point.y >= padding.top &&
        point.y <= canvas.clientHeight - padding.bottom
      );
    });
  };

  const correctTrailFitForTerrain = (
    trail: Trail,
    padding: ReturnType<typeof trailFitPadding>,
  ): void => {
    const correct = (): void => {
      if (destroyed || state.selectedTrailId !== trail.id || map.isMoving()) return;
      if (trailFitsViewport(trail, padding)) return;
      const center = map.getCenter();
      const bearing = map.getBearing();
      const pitch = map.getPitch();
      const maximumZoom = map.getZoom();
      let fittingZoom = map.getMinZoom();
      let overflowingZoom = maximumZoom;
      cameraEventsSuppressed = true;
      activeMovement = false;
      try {
        map.jumpTo({ center, zoom: fittingZoom, bearing, pitch });
        if (!trailFitsViewport(trail, padding)) return;
        for (let step = 0; step < 14; step += 1) {
          const candidateZoom = (fittingZoom + overflowingZoom) / 2;
          map.jumpTo({ center, zoom: candidateZoom, bearing, pitch });
          if (trailFitsViewport(trail, padding)) fittingZoom = candidateZoom;
          else overflowingZoom = candidateZoom;
        }
        map.jumpTo({
          center,
          zoom: Math.max(map.getMinZoom(), fittingZoom - 0.02),
          bearing,
          pitch,
        });
      } finally {
        cameraEventsSuppressed = false;
        state = Object.freeze({ ...state, camera: camera() });
        emit({ type: 'camera-changed', camera: state.camera, movement: 'completed' });
      }
    };
    if (map.loaded() && !map.isMoving()) requestAnimationFrame(correct);
    else map.once('idle', correct);
  };

  const selectTrail = (
    trail: Trail,
    coordinate: MapState['camera']['center'],
    fit: boolean,
  ): void => {
    if (state.selectedTrailId !== null) {
      map.removeFeatureState({ source: 'canonical-trails', id: state.selectedTrailId }, 'selected');
    }
    map.setFeatureState({ source: 'canonical-trails', id: trail.id }, { selected: true });
    state = Object.freeze({ ...state, selectedTrailId: trail.id });
    if (fit) {
      const padding = trailFitPadding();
      enableCameraEvents();
      const bounds = trailBounds(trail);
      map.fitBounds([coordinateTuple(bounds.southWest), coordinateTuple(bounds.northEast)], {
        padding,
        maxZoom: 16,
        duration: 0,
      });
      correctTrailFitForTerrain(trail, padding);
    }
    openTrailPopup(trail, coordinate);
    emit({ type: 'trail-selected', trailId: trail.id, coordinate });
  };
  const mapContainer = map.getContainer();
  for (const eventType of ['keydown', 'pointerdown', 'touchstart', 'wheel'] as const) {
    mapContainer.addEventListener(eventType, enableCameraEvents, { passive: true });
    handlerCount += 1;
    cleanup.push(() => {
      mapContainer.removeEventListener(eventType, enableCameraEvents);
    });
  }
  const reportRecovery = (): void => {
    const terrain = map.getTerrain();
    const expectedSourceIds = expectedOwnedSourceIds(options, state.basemap);
    const ownedSourceIds = Object.keys(map.getStyle().sources).filter((id) =>
      expectedSourceIds.has(id),
    );
    const expectedLayerIds = expectedOwnedLayerIds(options, state.basemap);
    const ownedLayerIds = map.getLayersOrder().filter((id) => expectedLayerIds.has(id));
    const visibleLayerIds = ownedLayerIds.filter(
      (id) => map.getLayoutProperty(id, 'visibility') !== 'none',
    );
    testHooks.styleRecovered?.(
      Object.freeze({
        basemap: state.basemap,
        boundsMask:
          map.getLayer('maximum-bounds-mask-fill') === undefined
            ? null
            : Object.freeze({
                fillColor: map.getPaintProperty('maximum-bounds-mask-fill', 'fill-color'),
                fillOpacity: map.getPaintProperty('maximum-bounds-mask-fill', 'fill-opacity'),
                filter: map.getFilter('maximum-bounds-mask-fill'),
              }),
        cantonOverlay:
          map.getLayer('cantons-fill') === undefined
            ? null
            : Object.freeze({
                fillColor: map.getPaintProperty('cantons-fill', 'fill-color'),
                fillOpacity: map.getPaintProperty('cantons-fill', 'fill-opacity'),
                filter: map.getFilter('cantons-fill'),
              }),
        handlerCount,
        layerIds: Object.freeze(ownedLayerIds),
        legacyLabelLayout: Object.freeze({
          bikeRegionAnchor: map.getLayoutProperty('bike-region-label', 'text-anchor'),
          zugerbergbahnLetterSpacing: map.getLayoutProperty(
            'zugerbergbahn-route-label',
            'text-letter-spacing',
          ),
          zugerbergbahnPadding: map.getLayoutProperty('zugerbergbahn-route-label', 'text-padding'),
        }),
        locale: state.locale,
        pitch: map.getPitch(),
        presentation: state.presentation,
        selectedTrailId: state.selectedTrailId,
        sourceIds: Object.freeze(ownedSourceIds),
        styleLoadCount,
        terrain:
          terrain === null
            ? null
            : Object.freeze({
                source: terrain.source,
                exaggeration: terrain.exaggeration ?? 1,
              }),
        visibleLayerIds: Object.freeze(visibleLayerIds),
      }),
    );
  };
  on('style.load', () => {
    try {
      styleLoadCount += 1;
      installApplicationLayers(map, options, state, providers);
      reportRecovery();
      becomeReadyWhenApplicationSourcesLoad();
    } catch {
      emit({
        type: 'error',
        error: createApplicationError({
          code: 'data-invalid',
          diagnostics: { operation: 'render' },
        }),
      });
    }
  });
  on('sourcedata', becomeReadyWhenApplicationSourcesLoad);
  on('movestart', () => {
    if (ready && cameraEventsEnabled && !cameraEventsSuppressed) activeMovement = true;
  });
  on('move', () => {
    if (!activeMovement) return;
    state = Object.freeze({ ...state, camera: camera() });
    emit({ type: 'camera-changed', camera: state.camera, movement: 'moving' });
  });
  on('moveend', () => {
    if (!activeMovement) return;
    activeMovement = false;
    state = Object.freeze({ ...state, camera: camera() });
    emit({ type: 'camera-changed', camera: state.camera, movement: 'completed' });
  });
  on('pitch', () => {
    const presentation = map.getPitch() > 0 ? 'terrain' : 'flat';
    if (presentation === state.presentation) return;
    state = Object.freeze({ ...state, presentation });
    emit({ type: 'presentation-changed', presentation });
  });
  on('error', (event) => {
    emit({
      type: 'error',
      error: classifyMapLibreProviderError(
        (event as unknown as { readonly sourceId?: string }).sourceId,
        event.error.message,
      ),
    });
  });
  const onGeolocate = (event: GeolocatePositionEvent): void => {
    try {
      const coordinate = createLngLat(event.coords.longitude, event.coords.latitude);
      const heading = event.coords.heading;
      emit({
        type: 'geolocation-result',
        coordinate,
        accuracy: event.coords.accuracy,
        ...(heading === null || !Number.isFinite(heading) ? {} : { heading }),
      });
    } catch {
      emit({ type: 'geolocation-error', reason: 'unavailable' });
    }
  };
  const onGeolocationError = (error: GeolocateErrorEvent): void => {
    emit({ type: 'geolocation-error', reason: geolocationFailureReason(error.code) });
  };
  controls.geolocation.on('geolocate', onGeolocate);
  controls.geolocation.on('error', onGeolocationError);
  cleanup.push(() => {
    controls.geolocation.off('geolocate', onGeolocate);
    controls.geolocation.off('error', onGeolocationError);
  });
  on('click', (event: MapMouseEvent) => {
    if (!ready) return;
    const coordinate = createLngLat(event.lngLat.lng, event.lngLat.lat);
    if (state.interactionMode === 'pick-location') {
      emit({ type: 'point-picked', coordinate });
      return;
    }
    if (map.getLayer('trail-hit-target') === undefined) return;
    const features = map.queryRenderedFeatures(event.point, { layers: ['trail-hit-target'] });
    if (features.length === 0) return;
    const properties = features[0]?.properties as unknown;
    const value =
      typeof properties === 'object' && properties !== null && 'trailId' in properties
        ? (properties as Readonly<Record<string, unknown>>)['trailId']
        : undefined;
    if (typeof value !== 'string') return;
    let trailId;
    try {
      trailId = createTrailId(value);
    } catch {
      return;
    }
    const trail = options.content.selectableTrails.find(({ id }) => id === trailId);
    if (trail !== undefined) selectTrail(trail, coordinate, false);
  });
  on('mousemove', (event: MapMouseEvent) => {
    if (
      !ready ||
      state.interactionMode !== 'browse' ||
      map.getLayer('trail-hit-target') === undefined
    )
      return;
    map.getCanvas().style.cursor =
      map.queryRenderedFeatures(event.point, { layers: ['trail-hit-target'] }).length > 0
        ? 'pointer'
        : '';
  });

  function visibility(group: LayerGroupDefinition, visible: boolean): void {
    for (const part of group.parts) {
      if (map.getLayer(part.id) !== undefined) {
        map.setLayoutProperty(part.id, 'visibility', visible ? 'visible' : 'none');
      }
    }
  }

  function apply(command: MapCommand): MapCommandResult {
    if (command.type === 'set-camera') {
      enableCameraEvents();
      map.jumpTo({
        center: coordinateTuple(command.camera.center),
        zoom: command.camera.zoom,
        bearing: command.camera.bearing,
        pitch: command.camera.pitch,
      });
    } else if (command.type === 'fit-bounds') {
      enableCameraEvents();
      map.fitBounds(
        [coordinateTuple(command.bounds.southWest), coordinateTuple(command.bounds.northEast)],
        {
          ...(command.maximumZoom === undefined ? {} : { maxZoom: command.maximumZoom }),
          duration: 0,
        },
      );
    } else if (command.type === 'set-basemap') {
      if (command.basemap === state.basemap) return { status: 'applied' };
      state = Object.freeze({ ...state, basemap: command.basemap });
      // MapLibre 6.6 can render one stale terrain frame against disposed style
      // shaders unless adapter-added terrain is detached before a full swap.
      map.setTerrain(null);
      map.setStyle(styleForBasemap(command.basemap, providers));
      emit({ type: 'basemap-changed', basemap: command.basemap });
    } else if (command.type === 'set-layer-visibility') {
      const group = options.content.layers.find(({ id }) => id === command.layer);
      if (group === undefined) {
        return {
          status: 'rejected',
          error: createApplicationError({
            code: 'unexpected',
            diagnostics: { operation: 'layer' },
          }),
        };
      }
      state = Object.freeze({
        ...state,
        layerVisibility: createLayerVisibilityState({
          ...state.layerVisibility,
          [command.layer]: command.visible,
        }),
      });
      visibility(group, command.visible);
      emit({ type: 'layer-visibility-changed', layer: command.layer, visible: command.visible });
    } else if (command.type === 'set-locale') {
      state = Object.freeze({ ...state, locale: command.locale });
      controls.setLocale(command.locale);
      map.getCanvas().setAttribute('aria-label', mapLibreUiLocale(command.locale)['Map.Title']);
      if (state.selectedTrailId !== null && selectedTrailPopup !== undefined) {
        const trail = options.content.selectableTrails.find(
          ({ id }) => id === state.selectedTrailId,
        );
        if (trail !== undefined && selectedTrailCoordinate !== undefined) {
          selectedTrailPopup.setDOMContent(createTrailPopupContent(trail, state.locale));
          localizePopupClose(selectedTrailPopup);
        }
      }
      emit({ type: 'locale-changed', locale: command.locale });
    } else if (command.type === 'resize') {
      map.resize();
    } else if (command.type === 'set-presentation') {
      enableCameraEvents();
      map.easeTo({ pitch: command.presentation === 'terrain' ? maximumPitch : 0 });
    } else if (command.type === 'request-geolocation') {
      const browserGeolocation: unknown = Reflect.get(navigator, 'geolocation');
      const unavailable = geolocationAvailabilityFailure(
        globalThis.isSecureContext,
        browserGeolocation !== undefined,
      );
      if (unavailable === undefined) controls.geolocation.trigger();
      else emit({ type: 'geolocation-error', reason: unavailable });
    } else if (command.type === 'retry-provider') {
      if (command.provider === 'analytics' || command.provider === 'trail-status') {
        return {
          status: 'rejected',
          error: createApplicationError({
            code: 'unexpected',
            diagnostics: { operation: 'provider-owner' },
          }),
        };
      }
      map.setTerrain(null);
      map.setStyle(styleForBasemap(state.basemap, providers));
    } else if (command.type === 'set-interaction-mode') {
      state = Object.freeze({ ...state, interactionMode: command.mode });
      emit({ type: 'interaction-mode-changed', mode: command.mode });
    } else if (command.type === 'select-trail') {
      const trail = options.content.selectableTrails.find(({ id }) => id === command.trailId);
      if (trail === undefined) return { status: 'not-found', entity: 'trail' };
      selectTrail(trail, trailAnchor(trail), command.fit === true);
    } else {
      clearTrailSelection();
    }
    return { status: 'applied' };
  }

  return Object.freeze({
    execute(command: MapCommand): Promise<MapCommandResult> {
      if (destroyed) {
        return Promise.resolve({
          status: 'rejected',
          error: createApplicationError({
            code: 'unexpected',
            diagnostics: { operation: 'session-destroyed' },
          }),
        });
      }
      if (
        command.type === 'select-trail' &&
        !options.content.selectableTrails.some(({ id }) => id === command.trailId)
      ) {
        return Promise.resolve({ status: 'not-found', entity: 'trail' });
      }
      if (!ready) {
        queued.push(command);
        return Promise.resolve({ status: 'queued' });
      }
      return Promise.resolve(apply(command));
    },
    subscribe(listener: (event: MapEvent) => void): () => void {
      if (destroyed) return () => undefined;
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      queued.length = 0;
      listeners.clear();
      removePopupSilently();
      map.getCanvas().style.cursor = '';
      for (const remove of cleanup.splice(0)) remove();
      map.remove();
    },
  });
}

export function createMapLibreMapAdapter(
  providers: MapLibreProviderConfig = defaultMapLibreProviders,
  testHooks: MapLibreTestHooks = {},
): MapEngineAdapter {
  return Object.freeze({
    id: 'maplibre',
    capabilities,
    mount(host: HTMLElement, options: MapMountOptions): Promise<MapSession> {
      try {
        validateContentSources(options);
        const initial = options.initialView;
        const map = new maplibregl.Map({
          container: host,
          style: styleForBasemap(defaultBasemap().id, providers),
          attributionControl: false,
          locale: mapLibreUiLocale(options.locale),
          maxBounds: [
            coordinateTuple(options.maximumBounds.southWest),
            coordinateTuple(options.maximumBounds.northEast),
          ],
          maxPitch: maximumPitch,
          ...(initial.type === 'camera'
            ? {
                center: coordinateTuple(initial.camera.center),
                zoom: initial.camera.zoom,
                bearing: initial.camera.bearing,
                pitch: initial.camera.pitch,
              }
            : {
                bounds: [
                  coordinateTuple(initial.bounds.southWest),
                  coordinateTuple(initial.bounds.northEast),
                ],
                fitBoundsOptions: {
                  ...(initial.maximumZoom === undefined ? {} : { maxZoom: initial.maximumZoom }),
                  padding: 30,
                },
              }),
        });
        const controls = createLocalizedMapControls(host, options.locale);
        map.addControl(controls.navigation, 'top-right');
        map.addControl(controls.fullscreen, 'top-right');
        map.addControl(controls.geolocation, 'top-right');
        controls.setLocale(options.locale);
        return Promise.resolve(createSession(map, options, providers, controls, testHooks));
      } catch (failure) {
        const applicationError = createApplicationError({
          code: failure instanceof TypeError ? 'data-invalid' : 'unexpected',
          diagnostics: { operation: 'map-mount' },
        });
        return Promise.reject(
          Object.assign(
            new Error(applicationError.messageId, { cause: failure }),
            applicationError,
          ),
        );
      }
    },
  });
}
